import React, { useCallback, useState } from 'react'
import { useLocation } from 'react-router'
import clsx from 'classnames'
import qs from 'qs'
import type { GiftCardRecipient, GiftCardsPage as GiftCardsPageData } from 'api/api.types'
import { APIService } from 'api/APIService'
import { parseHtml } from 'utils/htmlParserWithCustomLinks'
import { AppLink } from 'components/Utility/AppLink/AppLink'
import { StatusModal } from 'components/Utility/StatusModal/StatusModal'
import { Salons } from 'components/PageElements/Salons/Salons'
import visaLogo from 'src/assets/visa.png'
import mastercardLogo from 'src/assets/mastercard.png'
import mirLogo from 'src/assets/mir.svg'
import { GiftCardsList } from './GiftCardsList'
import { GiftCardsCart } from './GiftCardsCart'
import { GiftCardsStatus } from './GiftCardsStatus'
import type { Cart, CartItem } from './types'
import styles from './GiftCardsPage.module.scss'

type GiftCardsPageProps = {
  page: GiftCardsPageData,
}

const DOCUMENTS: Array<[key: 'rulesFile' | 'personalFile' | 'privacyFile' | 'paymentFile' | 'conditionsFile', title: string]> = [
  ['rulesFile', 'Условия использования электронных подарочных сертификатов «Пальчики»'],
  ['personalFile', 'Согласие на обработку персональных данных'],
  ['privacyFile', 'Политика конфиденциальности'],
  ['paymentFile', 'Порядок оплаты'],
  ['conditionsFile', 'Условия продажи товаров'],
]

const updateItem = (cart: Cart, index: number, change: (item: CartItem) => CartItem): Cart => ({
  ...cart,
  items: cart.items.map((item, i) => (i === index ? change(item) : item)),
})

/** Electronic gift cards: choose cards and recipients, pay at the bank, see the result on return */
export const GiftCardsPage: React.FC<GiftCardsPageProps> = ({ page }) => {
  const vars = page.templateVars.pageVars
  const [order, setOrder] = useState<Cart>({ items: [], discount: 0 })
  const [step, setStep] = useState<'list' | 'order'>('list')
  const [orderSubmitting, setOrderSubmitting] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)
  const location = useLocation()
  const { orderId } = qs.parse(location.search.replace('?', ''))

  const increaseAmount = useCallback((index: number) => {
    setOrder((cart) => updateItem(cart, index, (item) => ({ ...item, amount: item.amount + 1 })))
  }, [])

  const reduceAmount = useCallback((index: number) => {
    setOrder((cart) => updateItem(cart, index, (item) => ({ ...item, amount: Math.max(item.amount - 1, 1) })))
  }, [])

  const removeItem = useCallback((index: number) => {
    setOrder((cart) => ({ ...cart, items: cart.items.filter((_, i) => i !== index) }))
  }, [])

  const editRecipient = useCallback((index: number, recipient: GiftCardRecipient) => {
    setOrder((cart) => updateItem(cart, index, (item) => ({ ...item, recipient })))
  }, [])

  const addItem = useCallback((item: CartItem) => {
    setOrder((cart) => ({ ...cart, items: [...cart.items, item] }))
    setStep('order')
  }, [])

  const submitOrder = useCallback(async () => {
    setOrderSubmitting(true)
    try {
      const response = await APIService.certificates.order({
        ...order,
        items: order.items.map(({ certificate, amount, recipient }) => ({
          certificateId: certificate.id,
          amount,
          recipient,
        })),
      })
      if (response.formUrl) {
        window.location.href = response.formUrl
      }
    } catch (e) {
      setOrderError((e as Error).message)
    } finally {
      setOrderSubmitting(false)
    }
  }, [order])

  let content: React.ReactNode
  if (typeof orderId === 'string' && orderId) {
    content = <GiftCardsStatus orderId={orderId} />
  } else if (step === 'list') {
    content = <GiftCardsList certificates={vars.cards} onSelected={addItem} />
  } else {
    content = (
      <GiftCardsCart
        order={order}
        isSubmitting={orderSubmitting}
        onSubmit={submitOrder}
        onAddMore={() => setStep('list')}
        onIncreaseAmount={increaseAmount}
        onReduceAmount={reduceAmount}
        onRemove={removeItem}
        onEditRecipient={editRecipient}
      />
    )
  }

  return <div className={styles.container}>
    <h1 className={styles.title}>{page.title}</h1>
    {content}
    <div className={styles.contentContainer}>
      <div className={styles.content}>
        <article className={clsx([styles.article, 'text-content'])}>
          {parseHtml(vars.content || '')}
        </article>
        {DOCUMENTS.map(([key, title]) => {
          const file = vars[key]
          return file && <AppLink key={key} href={file.link} target={'_blank'}>{title}</AppLink>
        })}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img style={{ width: 64, marginRight: 16 }} src={visaLogo} alt={'Visa'} />
          <img style={{ width: 64, marginRight: 16 }} src={mastercardLogo} alt={'Mastercard'} />
          <img style={{ width: 64 }} src={mirLogo} alt={'МИР'} />
        </div>
      </div>
    </div>
    <Salons withCityTitle />
    <StatusModal
      isOpen={orderError !== null}
      status={'error'}
      modalText={orderError}
      buttonText={'ОК'}
      onClose={() => setOrderError(null)}
      onButtonClick={() => setOrderError(null)}
    />
  </div>
}
